import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import noop from 'lib/function/noop'
import usePrevious from 'hooks/usePrevious'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import type { DrawerExperience } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import TripPlanDetailDrawer from '../components/TripPlanDetailDrawer'
import SmartPlaceDrawer from '../components/SmartPlaceDrawer'
import PendingPlaceDrawer from '../components/PendingPlaceDrawer'
import LocationInfoDrawer from '../components/LocationInfoDrawer'
import ChatOfferDetailDrawer from '../components/ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from '../components/ChatExperienceDetailDrawer'
import ChatCruiseOfferDetailDrawer from '../components/CruiseOfferDetailDrawer'
import { useDrawerStackState } from '../hooks/useDrawerStackState'
import { useTripPlannerEventBus, useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { usePlaceSearchFailure } from 'tripPlanner/assistant/contexts/PlaceSearchFailureContext'
import { useRightPanelTripDrawerPresentation } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import { usePlaceSearchResolver } from '../hooks/usePlaceSearchResolver'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { trackHotelDrawerOpened, trackExperienceDrawerOpened, trackCruiseDrawerOpened } from 'tripPlanner/utils/chatAnalytics'
import { DrawerSnapshotRegistration } from '../contexts/TurnAuthoritySnapshotContext'
import { buildPlaceDrawerOpenedActivityEntry } from './buildPlaceDrawerOpenedActivityEntry'

interface DrawerContainerProps {
  closeSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
}

function DrawerContainer({
  closeSignal,
  onDrawerOpenChange = noop,
}: DrawerContainerProps = {}) {
  const eventBus = useTripPlannerEventBus()
  const [hasDomainDrawerBase, setHasDomainDrawerBase] = useState(false)
  const { sendMessage } = useChatActions()
  const { chatId, isStreaming, isStreamOpen } = useChatStreamStatus()
  const { itineraryData, multiCartId, deleteManualItineraryTarget, isManualItineraryDeletePending } = useItineraryContext()
  const manualDeleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending

  const {
    topEntry,
    previousEntry,
    isAnyDrawerOpen,
    hasPreviousDrawer,
    shouldAnimateTopEntry,
    openDetailPanel,
    openPlaceInfo,
    openPlaceSearchPending,
    resolvePlaceSearchPending,
    dismissPlaceSearchPending,
    openLocationInfo,
    openOfferDrawer,
    goBack,
    closeAllDrawers,
    afterClose,
    resetDrawers,
  } = useDrawerStackState()
  const [pendingSmartPlaceDrawerId, setPendingSmartPlaceDrawerId] = useState<string | undefined>(undefined)
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const showImmediatePlaceDrawerFeedback = drawerPresentation === 'modal'
  const checkoutSource = useMemo<App.CheckoutSource | undefined>(() => {
    if (!chatId || !multiCartId) return undefined
    return {
      type: 'trip_planner',
      multiCartId,
      externalReferenceId: String(chatId),
    }
  }, [chatId, multiCartId])

  const { resetFailures } = usePlaceSearchFailure()
  const handlePlaceSearchResolved = useCallback((googlePlaceId: string, placeName: string) => {
    resolvePlaceSearchPending({ googlePlaceId, placeName })
    if (!showImmediatePlaceDrawerFeedback) {
      openPlaceInfo({ googlePlaceId, placeName })
    }
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(placeName, 'trip_planner.chat.place_search_link')],
    })
  }, [eventBus, openPlaceInfo, resolvePlaceSearchPending, showImmediatePlaceDrawerFeedback])
  const { resolvePlace, cancel: cancelResolve } = usePlaceSearchResolver(
    handlePlaceSearchResolved,
    dismissPlaceSearchPending,
  )
  const previousCloseSignal = usePrevious(closeSignal, closeSignal)
  const previousIsAnyDrawerOpenRef = useRef(isAnyDrawerOpen)

  const closeDomainDrawers = useCallback(() => {
    setHasDomainDrawerBase(false)
    eventBus.dispatch({ type: 'close_right_panel_domain_drawers' })
  }, [eventBus])

  const handleSmartPlaceDrawerPendingChange = useCallback((entryId: string, isPending: boolean) => {
    setPendingSmartPlaceDrawerId(current => {
      if (isPending) return entryId
      return current === entryId ? undefined : current
    })
  }, [])

  useEffect(() => {
    if (!pendingSmartPlaceDrawerId || topEntry?.id === pendingSmartPlaceDrawerId) return

    setPendingSmartPlaceDrawerId(undefined)
  }, [pendingSmartPlaceDrawerId, topEntry?.id])

  useEffect(() => {
    if (previousIsAnyDrawerOpenRef.current === isAnyDrawerOpen) return

    previousIsAnyDrawerOpenRef.current = isAnyDrawerOpen
    onDrawerOpenChange(isAnyDrawerOpen)
  }, [isAnyDrawerOpen, onDrawerOpenChange])

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignal) return

    cancelResolve()
    resetDrawers()
    closeDomainDrawers()
  }, [cancelResolve, closeDomainDrawers, closeSignal, previousCloseSignal, resetDrawers])

  const trackedOfferDrawerIdsRef = useRef(new Set<string>())
  useEffect(() => {
    const isOfferDrawerOpen = isAnyDrawerOpen && topEntry?.kind === 'offerDrawer'
    const justOpened = isOfferDrawerOpen && !trackedOfferDrawerIdsRef.current.has(topEntry.id)
    if (!justOpened || !chatId) return

    trackedOfferDrawerIdsRef.current.add(topEntry.id)
    if (topEntry.data.offerType === 'experience') {
      trackExperienceDrawerOpened(chatId)
    } else if (topEntry.data.offerType === 'cruise') {
      trackCruiseDrawerOpened(chatId)
    } else {
      trackHotelDrawerOpened(chatId)
    }
  }, [isAnyDrawerOpen, topEntry, chatId])

  const handleDeleteStopFromDrawer = useCallback((stopId: number) => {
    if (manualDeleteActionsDisabled) return
    const stop = itineraryData?.stops.find((s) => s.id === stopId)
    if (!stop) return
    deleteManualItineraryTarget({ targetType: 'stop', stopId: stop.id })
  }, [manualDeleteActionsDisabled, itineraryData, deleteManualItineraryTarget])

  const closeEveryDrawer = useCallback(() => {
    cancelResolve()
    closeAllDrawers()
    closeDomainDrawers()
  }, [cancelResolve, closeAllDrawers, closeDomainDrawers])

  useEventSubscription('itinerary_item_added', () => {
    closeEveryDrawer()
  })

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    closeEveryDrawer()
    if (!hotel.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation,
        offerType: 'hotel',
      },
    })
  }, [closeEveryDrawer, eventBus])

  const handleExperienceDrawerShowMap = useCallback((experience: DrawerExperience) => {
    closeEveryDrawer()
    if (!experience.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: experience.offerId,
        name: experience.name,
        coordinates: experience.geoLocation,
        offerType: 'experience',
      },
    })
  }, [closeEveryDrawer, eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('open_place_drawer', (event) => {
    openPlaceInfo({ googlePlaceId: event.googlePlaceId, placeName: event.placeName })
  })

  useEventSubscription('open_place_search_drawer', (event) => {
    if (showImmediatePlaceDrawerFeedback) {
      openPlaceSearchPending({
        searchQuery: event.searchQuery,
        placeName: event.placeName,
      })
    }
    resolvePlace({ searchQuery: event.searchQuery, placeName: event.placeName })
  })

  useEventSubscription('open_location_drawer', (event) => {
    openLocationInfo(event.data)
  })

  useEventSubscription('open_detail_drawer', (event) => {
    openDetailPanel({
      googlePlaceId: event.googlePlaceId,
      placeName: event.placeName,
      countryName: event.countryName,
      stopId: event.stopId,
    })
  })

  useEventSubscription('open_offer_drawer', (event) => {
    openOfferDrawer({
      offerId: event.offerId,
      accommodationPropertyId: event.accommodationPropertyId,
      searchParams: event.searchParams,
      offerType: event.offerType,
      scrollToPricing: event.scrollToPricing,
      scrollToRoomOptions: event.scrollToRoomOptions,
    })
  })

  const handleStackBackNavigation = useCallback(() => {
    if (topEntry?.kind === 'placeSearchPending') {
      cancelResolve()
    }
    if (hasPreviousDrawer) {
      goBack()
      return
    }
    if (hasDomainDrawerBase) {
      closeAllDrawers()
    }
  }, [
    cancelResolve,
    closeAllDrawers,
    goBack,
    hasDomainDrawerBase,
    hasPreviousDrawer,
    topEntry?.kind,
  ])

  const handleStackBack = isAnyDrawerOpen && (hasPreviousDrawer || hasDomainDrawerBase) ?
    handleStackBackNavigation :
    undefined

  const centralDrawerAnimation = shouldAnimateTopEntry && !hasDomainDrawerBase ? undefined : 'none'
  const shouldDeferPendingSmartPlaceDrawer = !showImmediatePlaceDrawerFeedback &&
    topEntry?.kind === 'placeInfo' &&
    topEntry.id === pendingSmartPlaceDrawerId
  const visibleEntry = shouldDeferPendingSmartPlaceDrawer ? previousEntry : topEntry
  const visibleDrawerAnimation = visibleEntry?.id === topEntry?.id ? centralDrawerAnimation : 'none'

  useEventSubscription('right_panel_domain_drawer_state_changed', (event) => {
    setHasDomainDrawerBase(event.isOpen)
  })

  useEventSubscription('reset_chat_state', () => {
    cancelResolve()
    resetDrawers()
    closeDomainDrawers()
    resetFailures()
  })

  return (
    <>
      <DrawerSnapshotRegistration isAnyDrawerOpen={isAnyDrawerOpen} topEntry={topEntry} />
      {shouldDeferPendingSmartPlaceDrawer && topEntry?.kind === 'placeInfo' && (
        <SmartPlaceDrawer
          key={topEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation="none"
          googlePlaceId={topEntry.data.googlePlaceId}
          placeName={topEntry.data.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          chatId={chatId}
          drawerEntryId={topEntry.id}
          renderWhenReady={false}
          onPendingChange={handleSmartPlaceDrawerPendingChange}
        />
      )}
      {visibleEntry?.kind === 'detailPanel' && (
        <TripPlanDetailDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onDismiss={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          googlePlaceId={visibleEntry.data.googlePlaceId}
          placeName={visibleEntry.data.placeName}
          countryName={visibleEntry.data.countryName}
          stopId={visibleEntry.data.stopId}
          onDeleteStop={visibleEntry.data.stopId !== undefined && !manualDeleteActionsDisabled ? handleDeleteStopFromDrawer : undefined}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          chatId={chatId}
        />
      )}
      {visibleEntry?.kind === 'placeSearchPending' && (
        <PendingPlaceDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          placeName={visibleEntry.data.placeName}
        />
      )}
      {visibleEntry?.kind === 'placeInfo' && (
        <SmartPlaceDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          googlePlaceId={visibleEntry.data.googlePlaceId}
          placeName={visibleEntry.data.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          chatId={chatId}
          drawerEntryId={visibleEntry.id}
          onPendingChange={handleSmartPlaceDrawerPendingChange}
        />
      )}
      {visibleEntry?.kind === 'locationInfo' && (
        <LocationInfoDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          locationData={visibleEntry.data}
          category={visibleEntry.data.category}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {visibleEntry?.kind === 'offerDrawer' && visibleEntry.data.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          key={visibleEntry.id}
          offerId={visibleEntry.data.offerId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          onShowMap={handleExperienceDrawerShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          scrollToPricing={visibleEntry.data.scrollToPricing}
          checkoutSource={checkoutSource}
        />
      )}
      {visibleEntry?.kind === 'offerDrawer' && visibleEntry.data.offerType === 'cruise' && (
        <ChatCruiseOfferDetailDrawer
          key={visibleEntry.id}
          offerId={visibleEntry.data.offerId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          itineraryData={itineraryData}
          isStreaming={isStreaming || isStreamOpen}
          onSendMessage={sendMessage}
        />
      )}
      {visibleEntry?.kind === 'offerDrawer' && visibleEntry.data.offerType !== 'experience' && visibleEntry.data.offerType !== 'cruise' && (
        <ChatOfferDetailDrawer
          key={visibleEntry.id}
          offerId={visibleEntry.data.offerId}
          accommodationPropertyId={visibleEntry.data.accommodationPropertyId}
          offerType={visibleEntry.data.offerType}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          onShowMap={handleOfferShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          searchParams={visibleEntry.data.searchParams}
          scrollToRoomOptions={visibleEntry.data.scrollToRoomOptions}
          checkoutSource={checkoutSource}
        />
      )}
    </>
  )
}

export default DrawerContainer
